/**
 * API 封装层
 * 统一的 API 客户端，支持多后端服务
 * 包含请求/响应拦截器、Token 管理、错误处理
 */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import type { ApiResponse, SuccessResponse, ErrorResponse, PaginatedResponse } from '@/shared/types/api'
import { getErrorMessage, shouldHandleError, logError } from '@/shared/utils/error-handler'

/**
 * API 基础地址配置
 * 支持多后端服务（IAM、Agent Admin、Memory Admin等）
 */
const API_BASE_URLS = {
  iam: import.meta.env.VITE_IAM_API_URL || 'http://localhost:8000/api/v1',
  agentAdmin: import.meta.env.VITE_AGENT_ADMIN_API_URL || 'http://localhost:8001/api/v1',
  agentRuntime: import.meta.env.VITE_AGENT_RUNTIME_API_URL || 'http://localhost:8002/api/v1',
  memoryAdmin: import.meta.env.VITE_MEMORY_ADMIN_API_URL || 'http://localhost:8003/api/v1',
  memoryRuntime: import.meta.env.VITE_MEMORY_RUNTIME_API_URL || 'http://localhost:8004/api/v1',
}

/**
 * Token 存储键（统一命名）
 */
const TOKEN_KEY = 'axiom_access_token'
const REFRESH_TOKEN_KEY = 'axiom_refresh_token'

/**
 * Token 管理
 */
class TokenManager {
  /**
   * 获取 Token
   */
  static getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY)
  }

  /**
   * 设置 Token
   */
  static setToken(token: string): void {
    localStorage.setItem(TOKEN_KEY, token)
  }

  /**
   * 获取 Refresh Token
   */
  static getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY)
  }

  /**
   * 设置 Refresh Token
   */
  static setRefreshToken(refreshToken: string): void {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
  }

  /**
   * 清除 Token
   */
  static clearTokens(): void {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(REFRESH_TOKEN_KEY)
  }

  /**
   * 检查是否有 Token
   */
  static hasToken(): boolean {
    return !!this.getToken()
  }
}

/**
 * 创建 Axios 实例的工厂函数
 */
function createApiClient(baseURL: string): AxiosInstance {
  const client: AxiosInstance = axios.create({
    baseURL,
    timeout: 10000,
    headers: {
      'Content-Type': 'application/json',
    },
  })

  /**
   * 请求拦截器
   */
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      // 注入 Token
      const token = TokenManager.getToken()
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }

      // 生成请求 ID（用于链路追踪）
      const requestId = crypto.randomUUID()
      config.headers['X-Request-ID'] = requestId

      return config
    },
    (error) => {
      return Promise.reject(error)
    }
  )

  /**
   * 响应拦截器
   */
  client.interceptors.response.use(
    (response: AxiosResponse) => {
      // 添加响应日志（用于调试）
      // 如果是flow配置相关的请求，打印详细的edges数据
      const responseData = response.data as Record<string, any>
      const isFlowConfigRequest = response.config.url?.includes('/flow/projects') && response.config.url?.includes('/flow')
      if (isFlowConfigRequest && responseData?.data?.edges) {
        console.log('📥 [API] Flow配置响应详情:')
        console.log('  status:', response.status)
        console.log('  url:', response.config.url)
        console.log('  edgesCount:', responseData.data.edges.length)
        console.log('  edges:', JSON.stringify(responseData.data.edges, null, 2))
        console.log('  nodes:', JSON.stringify(responseData.data.nodes?.map((n: any) => ({ id: n.id, type: n.type })) || [], null, 2))
      }
      console.log('📥 [API] Response:', {
        status: response.status,
        url: response.config.url,
        baseURL: response.config.baseURL,
        data: response.data,
      })
      return response
    },
    async (error) => {
      // 处理 HTTP 错误
      if (error.response) {
        const errorData = error.response.data as ErrorResponse
        const status = error.response.status

        // 处理401认证错误：Access Token过期或无效
        // 标准流程：先尝试用Refresh Token刷新，刷新失败才强制退出
        if (status === 401) {
          // 检查是否有Refresh Token，如果有则尝试刷新
          const refreshToken = TokenManager.getRefreshToken()
          if (refreshToken) {
            console.log('🔄 [API] Access Token过期，尝试使用Refresh Token刷新')
            const refreshed = await tryRefreshToken()
            if (refreshed) {
              // 刷新成功，自动重试原请求（用户无感知）
              console.log('✅ [API] Token刷新成功，重试原请求')
              return client.request(error.config)
            }
            // 刷新失败（Refresh Token也过期或无效），继续执行强制退出逻辑
            console.warn('⚠️ [API] Refresh Token刷新失败，强制退出')
          } else {
            // 没有Refresh Token，直接强制退出
            console.warn('⚠️ [API] 没有Refresh Token，强制退出')
          }
          
          // Refresh Token也过期或无效，强制退出
          console.warn('⚠️ [API] 认证失败，强制退出:', {
            status,
            code: errorData.error?.code,
            message: errorData.error?.message,
            url: error.config?.url,
          })
          
          // 清除Token和用户状态
          TokenManager.clearTokens()
          
          // 清除authStore中的用户状态
          try {
            const { useAuthStore } = await import('@/shared/store/authStore')
            const authStore = useAuthStore.getState()
            await authStore.logout()
          } catch (e) {
            console.warn('⚠️ [API] 清除authStore失败:', e)
          }
          
          // 重定向到登录页
          window.location.href = '/login'
          return Promise.reject(error)
        }

        // 处理403权限错误：不是token过期问题，而是权限不足
        // 不应该尝试刷新token，直接返回错误
        if (status === 403) {
          console.warn('⚠️ [API] 权限不足（403）:', {
            code: errorData.error?.code,
            message: errorData.error?.message,
            url: error.config?.url,
          })
          // 403是权限问题，不是认证问题，不强制退出，只返回错误
          return Promise.reject(error)
        }

        // 处理需要重定向的错误（其他类型的错误）
        const { shouldRedirect, redirectTo } = shouldHandleError(errorData.error)
        if (shouldRedirect && redirectTo) {
          // 清除 Token 并重定向到登录页
          TokenManager.clearTokens()
          window.location.href = redirectTo
          return Promise.reject(error)
        }

        // 记录错误日志
        if (errorData.error) {
          logError(errorData.error, {
            url: error.config?.url,
            method: error.config?.method,
            baseURL: error.config?.baseURL,
          })
        } else {
          // 后端返回了HTTP错误但没有标准错误格式（可能是500内部错误）
          console.error('❌ [API] HTTP Error:', {
            status: status,
            statusText: error.response.statusText,
            url: error.config?.url,
            method: error.config?.method,
            baseURL: error.config?.baseURL,
            data: error.response.data,
          })
          
          // 对于500错误，提供更友好的错误信息
          if (status === 500) {
            const friendlyError = new Error(
              `服务器内部错误（${status}）：${error.config?.url || '未知接口'}。请检查后端日志或联系后端开发人员。`
            )
            friendlyError.name = 'ServerError'
            return Promise.reject(friendlyError)
          }
        }
      } else if (error.request) {
        // 网络错误（后端服务未启动或无法连接）
        // 注意：如果之前有 HTTP 响应（error.response），不应该走到这里
        // 只有在完全没有响应的情况下才是真正的网络错误
        
        // 检查是否是真正的网络错误（没有收到任何响应）
        const isRealNetworkError = !error.response && (
          error.code === 'ECONNREFUSED' || 
          error.code === 'ERR_NETWORK' || 
          error.code === 'ETIMEDOUT' ||
          error.message?.includes('Network Error') ||
          error.message?.includes('timeout')
        )
        
        if (isRealNetworkError) {
          console.error('❌ [API] Network Error:', {
            message: error.message,
            code: error.code,
            request: error.request,
            config: {
              url: error.config?.url,
              method: error.config?.method,
              baseURL: error.config?.baseURL,
              fullURL: `${error.config?.baseURL}${error.config?.url}`,
            },
          })
          
          // 从 baseURL 中提取主机和端口信息，用于错误提示
          const baseURL = error.config?.baseURL || '未知服务'
          let serviceInfo = baseURL
          try {
            const url = new URL(baseURL)
            serviceInfo = `${url.protocol}//${url.host}`
          } catch (e) {
            // 如果解析失败，使用原始 baseURL
          }
          const errorMessage = `无法连接到后端服务，请确认后端服务已启动（${serviceInfo}）`
          console.error('❌ [API]', errorMessage)
          const networkError = new Error(errorMessage)
          networkError.name = 'NetworkError'
          return Promise.reject(networkError)
        }
        
        // 如果不是真正的网络错误，继续传递原始错误
        console.warn('⚠️ [API] Request error (not a real network error):', {
          message: error.message,
          code: error.code,
          hasResponse: !!error.response,
        })
      } else {
        // 其他错误
        console.error('❌ [API] Error:', error.message)
      }

      return Promise.reject(error)
    }
  )

  return client
}

/**
 * 创建各服务的 API 客户端实例
 */
const iamClient = createApiClient(API_BASE_URLS.iam)
const agentAdminClient = createApiClient(API_BASE_URLS.agentAdmin)
const agentRuntimeClient = createApiClient(API_BASE_URLS.agentRuntime)
const memoryAdminClient = createApiClient(API_BASE_URLS.memoryAdmin)
const memoryRuntimeClient = createApiClient(API_BASE_URLS.memoryRuntime)

/**
 * 尝试刷新 Token
 */
let isRefreshing = false
let refreshSubscribers: Array<{
  onSuccess: (token: string) => void
  onError: (error: Error) => void
}> = []

async function tryRefreshToken(): Promise<boolean> {
  if (isRefreshing) {
    // 如果正在刷新，等待刷新完成
    return new Promise((resolve, reject) => {
      refreshSubscribers.push({
        onSuccess: (token) => {
          TokenManager.setToken(token)
          resolve(true)
        },
        onError: (error) => {
          reject(error)
        },
      })
    })
  }

  const refreshToken = TokenManager.getRefreshToken()
  if (!refreshToken) {
    return false
  }

  isRefreshing = true

  try {
    console.log('🔄 [TokenRefresh] 开始刷新 Token...')
    const response = await axios.post<ApiResponse<{ 
      access_token: string
      token_type?: string
      expires_in?: number
      refresh_token?: string // 后端可能返回新的 refresh_token
    }>>(
      `${API_BASE_URLS.iam}/refresh`,
      { refresh_token: refreshToken }
    )

    console.log('🔄 [TokenRefresh] 刷新响应:', response.data)

    if (response.data.success) {
      const data = (response.data as SuccessResponse<{ 
        access_token: string
        token_type?: string
        expires_in?: number
        refresh_token?: string
      }>).data
      
      // 后端返回 access_token，不是 token
      const newAccessToken = data.access_token
      if (!newAccessToken) {
        console.error('❌ [TokenRefresh] 响应中没有 access_token')
        throw new Error('No access_token in response')
      }
      
      TokenManager.setToken(newAccessToken)
      
      // 如果后端返回了新的 refresh_token，也更新它
      if (data.refresh_token) {
        TokenManager.setRefreshToken(data.refresh_token)
      }
      // 注意：如果后端不返回新的 refresh_token，保留原来的

      console.log('✅ [TokenRefresh] Token 刷新成功')

      // 通知所有等待的请求刷新成功
      refreshSubscribers.forEach((subscriber) => subscriber.onSuccess(newAccessToken))
      refreshSubscribers = []

      isRefreshing = false
      return true
    } else {
      // 刷新失败
      console.error('❌ [TokenRefresh] 刷新失败，响应 success=false')
      const error = new Error('Token refresh failed')
      refreshSubscribers.forEach((subscriber) => subscriber.onError(error))
      refreshSubscribers = []
      TokenManager.clearTokens()
      isRefreshing = false
      return false
    }
  } catch (error) {
    console.error('❌ [TokenRefresh] 刷新异常:', error)
    TokenManager.clearTokens()
    
    const refreshError = error instanceof Error ? error : new Error('Token refresh failed')
    refreshSubscribers.forEach((subscriber) => subscriber.onError(refreshError))
    refreshSubscribers = []
    
    isRefreshing = false
    return false
  }
}

/**
 * API 请求封装类
 * 支持多后端服务
 */
export class ApiClient {
  /**
   * GET 请求（使用默认客户端 - Agent Admin）
   */
  static async get<T = unknown>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<SuccessResponse<T>> {
    return this.getWithClient(agentAdminClient, url, config)
  }

  /**
   * POST 请求（使用默认客户端 - Agent Admin）
   */
  static async post<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<SuccessResponse<T>> {
    return this.postWithClient(agentAdminClient, url, data, config)
  }

  /**
   * PATCH 请求（使用默认客户端 - Agent Admin）
   */
  static async patch<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<SuccessResponse<T>> {
    return this.patchWithClient(agentAdminClient, url, data, config)
  }

  /**
   * PUT 请求（使用默认客户端 - Agent Admin）
   */
  static async put<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<SuccessResponse<T>> {
    return this.putWithClient(agentAdminClient, url, data, config)
  }

  /**
   * DELETE 请求（使用默认客户端 - Agent Admin）
   */
  static async delete<T = unknown>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<SuccessResponse<T>> {
    return this.deleteWithClient(agentAdminClient, url, config)
  }

  /**
   * 使用指定客户端发送 GET 请求
   */
  static async getWithClient<T = unknown>(
    client: AxiosInstance,
    url: string,
    config?: AxiosRequestConfig
  ): Promise<SuccessResponse<T>> {
    const response = await client.get<ApiResponse<T>>(url, config)
    return this.handleResponse(response)
  }

  /**
   * 使用指定客户端发送 POST 请求
   */
  static async postWithClient<T = unknown>(
    client: AxiosInstance,
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<SuccessResponse<T>> {
    const response = await client.post<ApiResponse<T>>(url, data, config)
    return this.handleResponse(response)
  }

  /**
   * 使用指定客户端发送 PATCH 请求
   */
  static async patchWithClient<T = unknown>(
    client: AxiosInstance,
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<SuccessResponse<T>> {
    const response = await client.patch<ApiResponse<T>>(url, data, config)
    return this.handleResponse(response)
  }

  /**
   * 使用指定客户端发送 PUT 请求
   */
  static async putWithClient<T = unknown>(
    client: AxiosInstance,
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<SuccessResponse<T>> {
    const response = await client.put<ApiResponse<T>>(url, data, config)
    return this.handleResponse(response)
  }

  /**
   * 使用指定客户端发送 DELETE 请求
   */
  static async deleteWithClient<T = unknown>(
    client: AxiosInstance,
    url: string,
    config?: AxiosRequestConfig
  ): Promise<SuccessResponse<T>> {
    const response = await client.delete<ApiResponse<T>>(url, config)
    return this.handleResponse(response)
  }

  /**
   * 使用指定客户端发送 GET 请求（分页响应）
   * 用于处理返回 PaginatedResponse 的接口
   */
  static async getPaginatedWithClient<T = unknown>(
    client: AxiosInstance,
    url: string,
    config?: AxiosRequestConfig
  ): Promise<PaginatedResponse<T>> {
    const response = await client.get<PaginatedResponse<T> | ErrorResponse>(url, config)
    return this.handlePaginatedResponse(response)
  }

  /**
   * 处理响应
   */
  private static handleResponse<T>(response: AxiosResponse<ApiResponse<T>>): SuccessResponse<T> {
    const data = response.data

    if (data.success) {
      return data as SuccessResponse<T>
    } else {
      const error = (data as ErrorResponse).error
      const message = getErrorMessage(error)
      // 创建一个包含原始响应信息的错误对象，以便调用方能够访问status和data
      const apiError: any = new Error(message)
      apiError.response = {
        status: response.status,
        statusText: response.statusText,
        data: data,
      }
      apiError.isAxiosError = true
      throw apiError
    }
  }

  /**
   * 处理分页响应
   */
  private static handlePaginatedResponse<T>(
    response: AxiosResponse<PaginatedResponse<T> | ErrorResponse>
  ): PaginatedResponse<T> {
    const data = response.data

    if (data.success) {
      return data as PaginatedResponse<T>
    } else {
      const error = (data as ErrorResponse).error
      const message = getErrorMessage(error)
      throw new Error(message)
    }
  }
}

/**
 * 导出各服务的客户端实例（用于特殊场景，如 SSE）
 */
export const apiClients = {
  iam: iamClient,
  agentAdmin: agentAdminClient,
  agentRuntime: agentRuntimeClient,
  memoryAdmin: memoryAdminClient,
  memoryRuntime: memoryRuntimeClient,
}

/**
 * 导出 Token 管理器
 */
export { TokenManager }

/**
 * 导出 API 基础地址配置
 */
export { API_BASE_URLS }

